/**
 * usePushNotifications — Capacitor-only hook that initializes OneSignal,
 * requests permission, links the OneSignal subscription to our MongoDB user
 * via `external_id`, and handles foreground / tap events.
 *
 * Web (PWA) is a no-op: web push goes through OneSignal's own JS SDK on a
 * separate path that we don't need for the Android/iOS app.
 */
import { useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';

const ONESIGNAL_APP_ID = (process.env.REACT_APP_ONESIGNAL_APP_ID || '').trim();

function isCapacitorNative() {
  return typeof window !== 'undefined' && (
    window.Capacitor?.isNativePlatform?.() === true ||
    /capacitor:\/\//i.test(window.location.protocol || '')
  );
}

export default function usePushNotifications() {
  const initRef = useRef(false);
  const linkedUserRef = useRef(null);
  const { user } = useAuth();

  // ---- 1. One-time init ----
  useEffect(() => {
    if (!ONESIGNAL_APP_ID) return;
    if (!isCapacitorNative() || initRef.current) return;
    initRef.current = true;

    function init() {
      try {
        const OneSignal = window.plugins?.OneSignal;
        if (!OneSignal || !OneSignal.Debug) {
          console.info('[push] OneSignal plugin not ready, retrying in 1s');
          setTimeout(init, 1000);
          return;
        }

        OneSignal.Debug.setLogLevel(6);
        OneSignal.initialize(ONESIGNAL_APP_ID);

        // Register the click listener as the FIRST thing after initialize().
        // OneSignal v5 caches a cold-start tap natively and replays it as
        // soon as the JS listener attaches, but the window between init()
        // and listener registration is where a tap can be dropped if anything
        // throws in between — so we keep this block before optIn / location
        // gating / foregroundWillDisplay registration.
        OneSignal.Notifications.addEventListener('click', (event) => {
          try {
            const data = event?.notification?.additionalData || {};
            const orderId = data.order_id;
            const deepLink = data.deep_link;
            const notificationId = data.notification_id;
            console.info('[push] click event received', { orderId, notificationId, hasDeepLink: !!deepLink });
            // Mark the matching in-app bell row read so the unread badge
            // updates immediately on tap. Fire-and-forget — does not block
            // navigation. Works for both admin-broadcast and order-event
            // notifications (the latter now also write an in-app row via
            // `deliver_order_notification` — Business Rule #13).
            if (notificationId) {
              try {
                const token = localStorage.getItem('token');
                if (token) {
                  fetch(`${process.env.REACT_APP_BACKEND_URL}/api/notifications/me/mark-read-by-notification-id`, {
                    method: 'POST',
                    headers: {
                      'Content-Type': 'application/json',
                      Authorization: `Bearer ${token}`,
                    },
                    body: JSON.stringify({ notification_id: notificationId }),
                  }).catch(() => {});
                }
              } catch { /* ignore */ }
            }
            const targetUrl = orderId ? `/order-confirmed/${orderId}` : deepLink;
            if (targetUrl) {
              // Hard nav so the route resolves whether or not BrowserRouter
              // has finished mounting (cold-start tap can fire very early).
              window.location.href = targetUrl;
            }
          } catch (e) {
            console.info('[push] click handler failed:', e?.message);
          }
        });

        OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event) => {
          try {
            const n = event?.notification;
            import('sonner').then(({ toast }) => {
              toast.success(n?.title || 'Update', { description: n?.body });
            });
          } catch { /* ignore */ }
        });

        // optIn() both requests OS permission (if not yet granted) AND
        // registers the subscription with OneSignal's server (flips
        // notification_types to 1). requestPermission() alone only shows
        // the OS dialog without updating subscription status.
        function doOptIn() {
          try {
            OneSignal.User.pushSubscription.optIn();
            console.info('[push] optIn called');
          } catch (e) {
            console.info('[push] optIn failed:', e?.message);
          } finally {
            // Signal that the OS push-permission flow has settled (success OR
            // failure) so Landing can sequence the out-of-city modal AFTER it
            // — the two native dialogs/modals must never overlap. Fires once.
            try { window.dispatchEvent(new Event('pb-push-resolved')); } catch { /* ignore */ }
          }
        }

        // Wait for the location permission dialog to resolve before showing
        // the notification dialog — Android only shows one at a time. This
        // gating intentionally applies ONLY to optIn(), never to the click
        // listener above, so cold-start taps are processed even on the very
        // first launch when location permission is still pending.
        if (sessionStorage.getItem('pb-location-resolved')) {
          doOptIn();
        } else {
          function optInAfterLocation() {
            window.removeEventListener('pb-location-resolved', optInAfterLocation);
            doOptIn();
          }
          window.addEventListener('pb-location-resolved', optInAfterLocation);
        }
      } catch (e) {
        console.info('[push] OneSignal init failed:', e?.message);
      }
    }

    init();
  }, []);

  // ---- 2. Link / unlink the OneSignal subscription with our user.id ----
  useEffect(() => {
    if (!ONESIGNAL_APP_ID) return;            // OneSignal paused
    if (!isCapacitorNative()) return;
    const currentId = user?.id || null;
    if (linkedUserRef.current === currentId) return;
    linkedUserRef.current = currentId;

    try {
      const OneSignal = window.plugins?.OneSignal;
      if (!OneSignal) return;
      if (currentId) {
        OneSignal.login(currentId);
        console.info('[push] OneSignal external_id linked:', currentId);
      } else {
        OneSignal.logout();
      }
    } catch (e) {
      console.info('[push] OneSignal login/logout failed:', e?.message);
    }
  }, [user?.id]);
}
